import React, {useState} from 'react';
import {
  View,
  Text,
  StyleSheet,
  Pressable,
  StyleProp,
  ViewStyle,
  Platform,
} from 'react-native';
import Styles from 'src/components/Styles';
import CustomToggleSwitch from 'src/components/elements/CustomToggleSwitch';

type SwitchProps = {
  testID: string;
  accessibilityLabel: string;
  accessibilityLabelledBy?: string;
  accessibilityState: {[key: string]: boolean};
  maxFontSizeMultiplier?: number;
  text: string;
  linkText?: string;
  value: boolean;
  onValueChange?: (value: boolean) => void;
  openLink?: () => void;
  disabled?: boolean;
  textColor?: string;
  stylesContainer?: StyleProp<ViewStyle>;
  toggleStyle?: boolean;
};

const SwitchComponent = (props: SwitchProps): JSX.Element => {
  const [isEnabled, setIsEnabled] = useState(false);
  const toggleSwitch = () => {
    setIsEnabled(!isEnabled);
    props.onValueChange(isEnabled);
  };
  const switchstyle = {
    ...styles.switch,
    ...(['setAsDefault', 'privacyPolicySwitch'].indexOf(props.testID) > -1 &&
    Platform.OS === 'web'
      ? {}
      : {flex: 0}),
  };
  return (
    <View style={[styles.container, props.stylesContainer]}>
      <View style={styles.textContainer}>
        <Text
          maxFontSizeMultiplier={
            props.maxFontSizeMultiplier || Styles.FontSizeMultiplier.maxfm8
          }
          accessible={true}
          accessibilityLabel={props.text}
          nativeId={props.text}
          aria-label={props.text}
          style={[
            styles.text,
            {color: props.textColor ? props.textColor : Styles.darkColor},
          ]}
        >
          {props.text}
        </Text>
        {
          // ie., Privacy Policy, User Agreement, ect
          props.linkText && (
            <Pressable
              accessibilityRole="link"
              role="link"
              onPress={props.openLink}
              accessibilityLabel={props.linkText}
              aria-label={props.linkText}
              nativeID={'BTN_Switch_' + props.linkText}
            >
              <Text
                nativeID={'Switch_' + props.linkText}
                maxFontSizeMultiplier={
                  props.maxFontSizeMultiplier ||
                  Styles.FontSizeMultiplier.maxfm8
                }
                style={styles.linkText}
              >
                {props.linkText}
              </Text>
            </Pressable>
          )
        }
      </View>
      <CustomToggleSwitch
        testID={props.testID}
        accessible={true}
        accessibilityLabel={props.accessibilityLabel}
        accessibilityHint="Double tap to toggle the switch"
        accessibilityState={{checked: isEnabled}}
        accessibilityLabelledBy={props.accessibilityLabelledBy}
        accessibilityRole="switch"
        role="switch"
        aria-label={props.accessibilityLabel}
        aria-checked={isEnabled}
        aria-labelledby={props.accessibilityLabelledBy}
        onValueChange={toggleSwitch}
        value={isEnabled}
        containerStyle={props.toggleStyle ? styles.toggleSwitch : switchstyle}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    //flex: 1, // fix for SOS-63089
    flexDirection: 'row',
    //justifyContent: 'space-between',
    alignItems: 'center',
    height: Styles.Heights.h4,
  },
  textContainer: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
  },
  switch: {
    backgroundColor: 'transparent',
    borderRadius: Styles.Spacing.m3,
    overflow: 'hidden',
    marginLeft: 12,
    justifyContent: 'flex-end',
    padding: 0,
  },
  linkText: {
    color: Styles.primaryColor,
    fontSize: Styles.Fonts.f1,
  },
  text: {
    fontSize: Styles.Fonts.f1,
    marginRight: Styles.Spacing.m1,
  },
  toggleSwitch: {
    flex: 0,
    marginRight: Platform.OS === 'web' ? 40 : -(Styles.Spacing.m2 + 2),
  },
});

export default SwitchComponent;
